import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import { LoadingIndicator } from "../common/VFX-UI";

//App
import Employee from "./Employee";
import organizationApi from "../../app/model/api/organizationApi";
import { PAGE_SIZE, queryKeys } from "./constants";
import {
  ContainerContentProps,
  FormikTypeaheadOption,
  SortBy,
} from "../../types";
import {
  OrganizationEmployeeContextData,
  OrganizationEmployeeState,
} from "../../types/organizationEmployee";
import { EMPTY_GUID } from "../../utility/constants/constants";
import {
  selectIsActive,
  setIsActive,
  selectSearchValue,
  setSearchValue,
} from "../../store/employee";
import { SearchValue } from "../../store/types";
import {
  IsOrganizationUser,
  getOrganizationId,
  hasPermission,
  permissions,
} from "../../utility/helpers/claimHelper";

export const EmployeeContext =
  React.createContext<OrganizationEmployeeContextData | null>(null);

const EmployeeContainer: React.FC<ContainerContentProps> = (props) => {
  const dispatch = useDispatch();
  const [state, setState] = useState<OrganizationEmployeeState>({
    employeeId: null,
    activeToggle: useSelector(selectIsActive) ?? true,
    searchPayload: useSelector(selectSearchValue) ?? null,
    pageSize: PAGE_SIZE,
    currentPage: 1,
    sortBy: [{ field: "firstName", direction: 1 }],
  });

  const lookupsQuery = useQuery({
    queryKey: [queryKeys.getEmployeeLookups],
    queryFn: () => organizationApi.getEmployeeLookups(),
    select: (data) => {
      const status: FormikTypeaheadOption[] | undefined =
        data?.result?.status?.map((status) => ({
          label: status.display as string,
          value: status.id as string,
        }));
      const type: FormikTypeaheadOption[] | undefined = data?.result?.type?.map(
        (type) => ({
          label: type.display as string,
          value: type.id as string,
        })
      );
      return {
        status: status,
        type: type,
      };
    },
  });

  const organizationEmployeesGridQuery = useQuery({
    queryKey: [queryKeys.getEmployees],
    queryFn: () =>
      organizationApi.searchEmployee({
        ...state.searchPayload,
        isActive: state.activeToggle,
        pageSize: state.pageSize,
        pageNumber: state.currentPage,
        sortBy: state.sortBy,
        organizationId: IsOrganizationUser()
          ? getOrganizationId()
          : window.location.pathname.split("/")[2] ?? EMPTY_GUID,
      }),
  });

  const employeeQuery = useQuery({
    queryKey: [queryKeys.getEmployee, state.employeeId],
    queryFn: () => organizationApi.getEmployee(state.employeeId ?? EMPTY_GUID),
    enabled: state.employeeId !== null,
    select: (data) => {
      const result = data?.result;
      let name = null;
      if (result?.lastName) name = result?.lastName + ", ";
      if (result?.firstName) {
        if (name) name += result?.firstName;
        else name = result?.firstName;
      }

      return {
        ...data?.result,
        name: name,
      };
    },
  });

  const hasManageEmployeesPermission = hasPermission([
    permissions.MANAGE_EMPLOYEES,
  ]);

  const updateEmployeeId = async (employeeId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      employeeId: employeeId,
    }));
  };

  const updateActiveToggle = async () => {
    dispatch(setIsActive());
    setState((prevState) => ({
      ...prevState,
      activeToggle: !prevState.activeToggle,
    }));
  };

  const updateSortBy = async (sortBy: SortBy) => {
    const { field, direction } = sortBy;
    setState((prevState) => ({
      ...prevState,
      sortBy: [{ field, direction }],
    }));
  };

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values?.name) {
      dispatch(setSearchValue({ name: values.name }));
    }
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
        sortBy: [{ field: "firstName", direction: 1 }],
      }));
    }
  };

  const updateCurrentPage = async (currentPage: number) => {
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));
  };

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

  const { mutate: deleteMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await organizationApi.deactivateEmployee(value);
    },
  });

  const { mutate: activateMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await organizationApi.activateEmployee(value);
    },
  });

  const handleResendEmail = async (employeeIds: string[]) => {
    let result = false;
    if (employeeIds?.length > 0) {
      try {
        const response = await organizationApi.sendPledgeReminderMail(
          employeeIds
        );
        if (response.isSuccess) result = true;
      } catch {}
    }
    return result;
  };

  const contextValue: OrganizationEmployeeContextData = {
    data: employeeQuery?.data,
    id: state.employeeId,
    setId: updateEmployeeId,
    lookups: lookupsQuery?.data,
    activeToggle: state.activeToggle,
    setActiveToggleValue: updateActiveToggle,
    searchValue: state.searchPayload ?? undefined,
    setSearchValue: updateSearchPayload,
    paging: {
      currentPage: state.currentPage,
      totalSize: organizationEmployeesGridQuery?.data?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
    setCurrentPageSize: updateCurrentPageSize,
    setCurrentPage: updateCurrentPage,
    setSortBy: updateSortBy,
    refetchGrid: () => organizationEmployeesGridQuery?.refetch(),
    hasManageEmployeesPermission: hasManageEmployeesPermission,
  };

  if (lookupsQuery?.isLoading) return <LoadingIndicator />;

  return (
    <EmployeeContext.Provider value={contextValue}>
      <Employee
        data={organizationEmployeesGridQuery?.data?.result}
        deleteMutation={deleteMutation}
        activateMutation={activateMutation}
        fullscreen={props.fullscreen}
        handleResendEmail={handleResendEmail}
      />
    </EmployeeContext.Provider>
  );
};

export default EmployeeContainer;
